import { describeMutationError } from "@api/mutationErrors";
import { useSessionKey } from "@auth/sessionStore";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import {
  Button,
  Checkbox,
  Dialog,
  FieldError,
  Select,
} from "@scribenote/brian-design-system";
import type { NormalizedBox } from "@users/staffPhotoClient";
import { useCaptureStaffPhotos } from "@users/staffPhotoQueries";
import { useClinicUsers } from "@users/userQueries";
import { useState } from "react";

import type { RecentStill } from "./perceptionClient";
import { useRecentStills } from "./perceptionQueries";
import { StillBoxEditor } from "./StillBoxEditor";
import { StillScrubber } from "./StillScrubber";

/** Mirrors brian `staff_photos.MAX_ROOM_SHOTS`; change both together. */
const MAX_SHOTS = 3;

interface Shot {
  still: RecentStill;
  box: NormalizedBox | null;
}

/**
 * Save a staff member's reference photos from one room's camera: scrub the
 * room's last two minutes of stills, choose up to three, and draw a box round the
 * person on each. The browser sends only the room, each still's reference and
 * its box; brian crops them from its own copies of those stills. Photos are
 * confirmed at once when the person has given their one-time consent, and wait
 * for it on My reference photos otherwise. The set replaces the person's
 * earlier camera photos from that room.
 *
 * The stills load once and never poll, so the one being scrubbed does not move;
 * Refresh loads a fresh two minutes.
 */
export const StaffPhotoCapture = ({
  roomId: presetRoomId,
  userId: presetUserId,
  onClose,
}: {
  roomId?: string;
  userId?: string;
  onClose: () => void;
}) => {
  const sessionKey = useSessionKey();
  const { data: staff } = useClinicUsers(sessionKey);
  const rooms = useRoomsListQuery(sessionKey, true, true);
  const capture = useCaptureStaffPhotos();
  const [roomId, setRoomId] = useState<string | null>(presetRoomId ?? null);
  const [userId, setUserId] = useState<string | null>(presetUserId ?? null);
  const [attested, setAttested] = useState(false);
  const [shots, setShots] = useState<Shot[]>([]);
  // null is the newest still, which a refresh moves to.
  const [position, setPosition] = useState<number | null>(null);
  const recent = useRecentStills(sessionKey, roomId, attested);
  const saved = capture.data;
  const stills = recent.data ?? [];
  const index = Math.min(position ?? stills.length - 1, stills.length - 1);
  const name = staff?.find((user) => user.id === userId)?.displayName;
  const confirmed = saved?.every((photo) => photo.status === "confirmed");

  const refresh = () => {
    setPosition(null);
    void recent.refetch().then(({ data }) =>
      setShots((current) =>
        current.map((shot) => {
          const fresh = data?.find((s) => s.reference === shot.still.reference);
          return fresh && fresh.signedAt > shot.still.signedAt
            ? { ...shot, still: fresh }
            : shot;
        }),
      ),
    );
  };

  const save = () => {
    if (!userId || !roomId) return;
    capture.mutate({
      userId,
      roomId,
      shots: shots.flatMap(({ still, box }) =>
        box ? [{ stillReference: still.reference, box }] : [],
      ),
    });
  };

  const ready =
    !!userId &&
    !!roomId &&
    attested &&
    shots.length > 0 &&
    shots.every((shot) => shot.box);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Save reference photos from a room camera"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {saved ? "Done" : "Cancel"}
          </Button>
          {!saved && (
            <Button
              variant="accent"
              disabled={!ready || capture.isPending}
              onClick={save}
            >
              {shots.length > 1
                ? `Save ${shots.length} reference photos`
                : "Save reference photo"}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-body">
          Choose the room and the staff member, then pick up to {MAX_SHOTS}{" "}
          stills from the last two minutes and draw a box round the person on
          each. Staff only, never clients. These photos replace this
          person&rsquo;s earlier ones from this room.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Room"
            value={roomId}
            onValueChange={(value) => {
              setRoomId(value);
              setShots([]);
              setPosition(null);
            }}
            placeholder="Choose a room"
            disabled={!!saved}
            options={(rooms.data ?? []).map((room) => ({
              value: room.id,
              label: room.name,
            }))}
          />
          <Select
            label="Staff member"
            value={userId}
            onValueChange={setUserId}
            placeholder="Choose a staff member"
            disabled={!!saved}
            options={(staff ?? []).map((user) => ({
              value: user.id,
              label: user.displayName,
            }))}
          />
        </div>
        {/* Placeholder wording, pending Ryan's approval with brian's CONSENT_TEXT. */}
        <Checkbox
          checked={attested}
          disabled={!!saved}
          onChange={(event) => setAttested(event.target.checked)}
          label="This person agreed to have these photos taken. Brian uses them once they have agreed on My reference photos."
        />

        {attested && roomId && (
          <section aria-label="Recent stills" className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-base font-medium text-ink">
                Last two minutes
              </h3>
              <Button
                variant="outline"
                size="sm"
                disabled={recent.isFetching || !!saved}
                onClick={refresh}
              >
                Refresh
              </Button>
            </div>
            {recent.isError ? (
              <FieldError>
                I couldn&rsquo;t load this room&rsquo;s stills.
              </FieldError>
            ) : recent.isPending ? (
              <p className="text-sm text-annotation">Loading the stills.</p>
            ) : stills.length === 0 ? (
              <p className="text-sm text-annotation">
                This room has sent no stills in the last two minutes.
              </p>
            ) : (
              <StillScrubber
                windowLabel="last two minutes"
                stills={stills}
                index={index}
                onIndex={setPosition}
                chosen={shots.some(
                  (shot) => shot.still.reference === stills[index]?.reference,
                )}
                full={shots.length >= MAX_SHOTS || !!saved}
                onChoose={(still) =>
                  setShots((current) => [...current, { still, box: null }])
                }
              />
            )}
          </section>
        )}

        {shots.length > 0 && (
          <section aria-label="Chosen stills" className="flex flex-col gap-2">
            <h3 className="font-display text-base font-medium text-ink">
              Draw a box round the person on each ({shots.length} of {MAX_SHOTS}
              )
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              {shots.map((shot, i) => (
                <div key={shot.still.reference} className="flex flex-col gap-1">
                  <StillBoxEditor
                    still={shot.still}
                    box={shot.box}
                    disabled={!!saved}
                    onChange={(box) =>
                      setShots((current) =>
                        current.map((s, j) => (j === i ? { ...s, box } : s)),
                      )
                    }
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-annotation">
                      {shot.box ? "Box drawn" : "Drag to draw a box"}
                    </span>
                    {!saved && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setShots((current) =>
                            current.filter((_, j) => j !== i),
                          )
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {capture.isError && (
          <FieldError>
            {describeMutationError(capture.error, "save the photos")}
          </FieldError>
        )}
        {saved && (
          <p className="text-sm text-ink-body">
            {confirmed
              ? `Saved. ${name ?? "They"} already agreed, so Brian uses ${saved.length === 1 ? "this photo" : "these photos"} now.`
              : `Saved. ${name ?? "They"} must agree once under My reference photos before Brian uses ${saved.length === 1 ? "this photo" : "these photos"}.`}
          </p>
        )}
      </div>
    </Dialog>
  );
};
